import { Heading, Button, Flex, Span } from '@chakra-ui/react';
import { getTranslation } from '../utils/translations';
import { formatUtcToTimezone, i18n } from '@shared';
import { useSurveyContext } from '../context/SurveyContext';
import { useTranslation } from 'react-i18next';
import { darkenHex, getReadableTextColor } from '../utils/colors';
import {
	BsEmojiAngryFill,
	BsEmojiAngry,
	BsEmojiFrownFill,
	BsEmojiFrown,
	BsEmojiNeutralFill,
	BsEmojiNeutral,
	BsEmojiSmileFill,
	BsEmojiSmile,
	BsEmojiLaughingFill,
	BsEmojiLaughing,
} from 'react-icons/bs';
import { IoArrowBack, IoStar, IoStarOutline } from 'react-icons/io5';
import { useFormContext } from 'react-hook-form';
import type { JSX } from 'react';

const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SubmissionSummaryPage({ isSubmitting, currentPage, setPage }: { isSubmitting: boolean; currentPage: number; setPage: (value: number) => void }) {
	const { t } = useTranslation();
	const { survey } = useSurveyContext();
	const { watch } = useFormContext();

	function getSmileyByIndex(index: number, isSelected: boolean): JSX.Element | undefined {
		const smileys = [
			isSelected ? <BsEmojiAngryFill size="35" fill={survey.colorScheme} /> : <BsEmojiAngry fill={survey.colorScheme} />,
			isSelected ? <BsEmojiFrownFill size="35" fill={survey.colorScheme} /> : <BsEmojiFrown fill={survey.colorScheme} />,
			isSelected ? <BsEmojiNeutralFill size="35" fill={survey.colorScheme} /> : <BsEmojiNeutral fill={survey.colorScheme} />,
			isSelected ? <BsEmojiSmileFill size="35" fill={survey.colorScheme} /> : <BsEmojiSmile fill={survey.colorScheme} />,
			isSelected ? <BsEmojiLaughingFill size="35" fill={survey.colorScheme} /> : <BsEmojiLaughing fill={survey.colorScheme} />,
		];

		return smileys[index];
	}

	function getStar(isSelected: boolean): JSX.Element {
		return isSelected ? <IoStar size="35" fill={survey.colorScheme} /> : <IoStarOutline size="35" color={survey.colorScheme} />;
	}

	function getSurveyElementOptionAnswers(element: SurveyElement, value: string | number[]): string {
		if (element.elementType === 'checkbox' && Array.isArray(value) && value.every((v): v is number => typeof v === 'number')) {
			// 0-n answers, so value is number []
			const answers: string[] = [];

			for (let index = 0; index < value.length; index++) {
				const translation = element.surveyElementOptions.find(option => option.surveyElementOptionID === (value[index] as number))?.translations;

				if (translation) {
					const answer = getTranslation(translation, 'surveyElementOptions.label', i18n.resolvedLanguage as Lang, true);
					answers.push(answer);
				}
			}

			if (answers.length === 0) {
				return '-';
			}
			return answers.join(', ');
		}

		return '';
	}
	return (
		<>
			<Heading className="tav-survey-check-answers-heading" marginBottom={4} color="black">
				{t('general.check_answers')}
			</Heading>
			<hr />

			<Flex className="tav-survey-last-page-container" marginTop={4} flexDirection={'column'} gap={4}>
				{Object.entries(watch()).map(([key, value]) => {
					const element = survey.surveyElements?.find(e => e.surveyElementID === parseInt(key));

					if (!element) {
						return null;
					}

					if (element.elementType === 'smiley') {
						return (
							<Flex flexDirection={'column'} key={key}>
								<Heading size="md">{getTranslation(element.translations, 'surveyElements.label', i18n.resolvedLanguage as Lang)}</Heading>
								{getSmileyByIndex(parseInt(value) - 1, true)}
							</Flex>
						);
					}

					if (element.elementType === 'star') {
						return (
							<Flex flexDirection={'column'} key={key}>
								<Heading size="md">{getTranslation(element.translations, 'surveyElements.label', i18n.resolvedLanguage as Lang)}</Heading>
								<Flex gap={2}>
									{getStar(parseInt(value) - 1 >= 0)}
									{getStar(parseInt(value) - 1 > 0)}
									{getStar(parseInt(value) - 1 > 1)}
									{getStar(parseInt(value) - 1 > 2)}
									{getStar(parseInt(value) - 1 > 3)}
								</Flex>
							</Flex>
						);
					}

					if (element.elementType === 'date') {
						return (
							<Flex flexDirection={'column'} key={key}>
								<Heading size="md">{getTranslation(element.translations, 'surveyElements.label', i18n.resolvedLanguage as Lang)}</Heading>

								<Span>{formatUtcToTimezone(value, TIMEZONE)}</Span>
							</Flex>
						);
					}

					return (
						<Flex flexDirection={'column'} key={key}>
							<Heading size="md">{getTranslation(element.translations, 'surveyElements.label', i18n.resolvedLanguage as Lang)}</Heading>

							{element?.elementType === 'checkbox' ? <Span>{getSurveyElementOptionAnswers(element, value)}</Span> : <Span>{value || '-'}</Span>}
						</Flex>
					);
				})}
			</Flex>
			<Flex className="tav-survey-last-page-buttons" justifyContent="space-between" marginTop="auto" paddingTop={4}>
				<Button
					className="tav-survey-last-page-buttons-prev"
					backgroundColor={survey.colorScheme}
					_hover={{
						backgroundColor: darkenHex(survey.colorScheme, 20),
					}}
					onClick={() => setPage(currentPage - 1)}
					color={getReadableTextColor(survey.colorScheme)}
				>
					<IoArrowBack />
					{t('general.previous')}
				</Button>
				<Button
					loading={isSubmitting}
					className="tav-survey-last-page-buttons-submit"
					backgroundColor={survey.colorScheme}
					_hover={{
						backgroundColor: darkenHex(survey.colorScheme, 20),
					}}
					type="submit"
					color={getReadableTextColor(survey.colorScheme)}
				>
					{t('general.submit')}
				</Button>
			</Flex>
		</>
	);
}
